import { useId, useState, type FormEvent, type ReactNode } from 'react';
import {
  ibanError,
  normalizeIban,
  paymentMethodKeys,
  paymentMethodLabels,
  IBAN_GERMAN_LENGTH_MESSAGE,
  type PaymentMethodBody,
  type PaymentMethodKey,
} from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import { Banner, Button, Checkbox } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { submitPaymentMethod } from '../api';
import styles from './billing-ui.module.css';

export interface PaymentMethodFormProps {
  /**
   * Sends the validated body. Default: `POST /settings/billing/payment-method`, which answers 409
   * `PAYMENTS_NOT_ACTIVE` while no payment provider exists (D-07) — the form shows that message as a banner.
   */
  onSubmit?: ((body: PaymentMethodBody) => Promise<void>) | undefined;
  /** Rendered at the top inside the card, e.g. the plan line (`<PlanCard variant="compact" />`). */
  header?: ReactNode | undefined;
  /** Left of the footer, e.g. „Zurück". */
  secondary?: ReactNode | undefined;
  /** Text button before the primary one, e.g. „Bis 30.09. nachholen" (Onboarding Nutzer 10). */
  deadlineLabel?: string | undefined;
  onDefer?: (() => void) | undefined;
  submitLabel?: string | undefined;
  defaultHolder?: string | undefined;
  defaultAddress?: string | undefined;
  defaultVatId?: string | undefined;
  /** Without the card chrome (inside a dialog). */
  bare?: boolean | undefined;
}

type FieldKey = 'holder' | 'iban' | 'address' | 'vatId' | 'mandate';

/** „DE44750500000" → „DE44 7505 0000" (groups of four while typing). */
function groupIban(raw: string): string {
  return normalizeIban(raw)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

/**
 * Payment method form (Onboarding Nutzer 10/M8, Einstellungen 19 „+ Zahlungsmittel hinzufügen"): tabs
 * „SEPA-Lastschrift" / „Kreditkarte" / „Rechnung", holder, IBAN with the length check of `@brimo/shared`,
 * billing address, optional VAT ID and the SEPA mandate. The primary button stays disabled until the form is
 * complete; server field errors land on their fields, every other answer (409) becomes a banner.
 */
export function PaymentMethodForm({
  onSubmit = submitPaymentMethod,
  header,
  secondary,
  deadlineLabel,
  onDefer,
  submitLabel = 'Speichern',
  defaultHolder = '',
  defaultAddress = '',
  defaultVatId = '',
  bare = false,
}: PaymentMethodFormProps) {
  const baseId = useId();
  const [method, setMethod] = useState<PaymentMethodKey>('sepa');
  const [holder, setHolder] = useState(defaultHolder);
  const [iban, setIban] = useState('');
  const [address, setAddress] = useState(defaultAddress);
  const [vatId, setVatId] = useState(defaultVatId);
  const [mandate, setMandate] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [serverErrors, setServerErrors] = useState<Partial<Record<FieldKey, string | undefined>>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const sepa = method === 'sepa';
  const localIbanError = sepa && iban.trim() !== '' ? ibanError(iban) : null;
  const complete =
    address.trim() !== '' &&
    (!sepa || (holder.trim() !== '' && iban.trim() !== '' && localIbanError === null && mandate));
  const ibanMessage = serverErrors.iban ?? (touched.iban ? localIbanError : null);

  function fieldId(key: FieldKey): string {
    return `${baseId}-${key}`;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!complete || pending) return;
    setPending(true);
    setBanner(null);
    setServerErrors({});
    const body: PaymentMethodBody = {
      method,
      holder: sepa ? holder.trim() : '',
      iban: sepa ? normalizeIban(iban) : '',
      address: address.trim(),
      vatId: vatId.trim() === '' ? null : vatId.trim(),
      mandate: sepa ? mandate : false,
    };
    try {
      await onSubmit(body);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        const fields = error.fields;
        setServerErrors({
          holder: fields['holder'],
          iban: fields['iban'],
          address: fields['address'],
          vatId: fields['vatId'],
          mandate: fields['mandate'],
        });
      } else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  function textField(
    key: Exclude<FieldKey, 'mandate'>,
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { placeholder?: string; error?: string | null; errorLabel?: string; autoComplete?: string } = {},
  ) {
    const error = options.error ?? serverErrors[key] ?? null;
    return (
      <div className={styles.field}>
        <label htmlFor={fieldId(key)} className={clsx(styles.fieldLabel, error && styles.fieldLabelError)}>
          {error && options.errorLabel ? options.errorLabel : label}
        </label>
        <input
          id={fieldId(key)}
          className={clsx(styles.input, error && styles.inputError)}
          value={value}
          placeholder={options.placeholder}
          autoComplete={options.autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId(key)}-error` : undefined}
          onChange={(event) => {
            onChange(event.target.value);
            if (serverErrors[key]) setServerErrors({ ...serverErrors, [key]: undefined });
          }}
          onBlur={() => setTouched({ ...touched, [key]: true })}
        />
        {error ? (
          <span id={`${fieldId(key)}-error`} className={styles.fieldError}>
            {error}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
      <div className={clsx(styles.formCard, bare && styles.formBare)}>
        {header}
        <div role="tablist" aria-label="Zahlart" className={styles.methodTabs}>
          {paymentMethodKeys.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={method === key}
              className={clsx(styles.methodTab, method === key && styles.methodTabActive)}
              onClick={() => {
                setMethod(key);
                setServerErrors({});
              }}
            >
              {paymentMethodLabels[key]}
            </button>
          ))}
        </div>
        <div className={styles.fields} role="tabpanel" aria-label={paymentMethodLabels[method]}>
          {sepa ? textField('holder', 'Kontoinhaber', holder, setHolder, { autoComplete: 'name' }) : null}
          {sepa
            ? textField('iban', 'IBAN', iban, (value) => setIban(groupIban(value)), {
                placeholder: 'DE00 0000 0000 0000 0000 00',
                error: ibanMessage,
                errorLabel: ibanMessage === IBAN_GERMAN_LENGTH_MESSAGE ? 'IBAN · unvollständig' : 'IBAN',
              })
            : null}
          {textField('address', 'Rechnungsanschrift', address, setAddress, {
            autoComplete: 'street-address',
          })}
          {textField('vatId', 'Umsatzsteuer-ID (optional)', vatId, setVatId, { placeholder: 'DE …' })}
        </div>
        {method === 'card' ? (
          <p className={styles.note}>
            Die Kartendaten erfassen Sie im Zahlungsfenster des Zahlungsanbieters.
          </p>
        ) : null}
        {method === 'invoice' ? (
          <p className={styles.note}>Die Rechnung kommt monatlich per E-Mail an die Rechnungsanschrift.</p>
        ) : null}
        {sepa ? (
          <div className={clsx(styles.mandate, serverErrors.mandate && styles.mandateInvalid)}>
            <Checkbox
              label="Ich ermächtige DAMOKLES, fällige Beträge per SEPA-Lastschrift einzuziehen."
              checked={mandate}
              onChange={(event) => {
                setMandate(event.target.checked);
                setServerErrors({ ...serverErrors, mandate: undefined });
              }}
            />
          </div>
        ) : null}
        {serverErrors.mandate ? <span className={styles.fieldError}>{serverErrors.mandate}</span> : null}
        {banner ? <Banner tone="info">{banner}</Banner> : null}
      </div>
      <div className={styles.formFooter}>
        {secondary}
        {deadlineLabel ? (
          <button type="button" className={styles.defer} onClick={onDefer}>
            {deadlineLabel}
          </button>
        ) : null}
        <Button
          type="submit"
          disabled={!complete}
          loading={pending}
          className={deadlineLabel ? undefined : styles.submitAlone}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
